import type { Chain } from 'viem'
import { base, baseSepolia, hardhat, mainnet } from 'wagmi/chains'
import { ENV } from './env'

// NOTE on Multicall3: wagmi's `useReadContracts` batches into a single RPC
// call when the active chain config exposes `contracts.multicall3` AND the
// contract is actually deployed at that address. Without the config, wagmi
// falls back to N separate `eth_call`s — slower but functional. Configuring
// the contract on a chain where it is NOT deployed BREAKS reads (aggregate3
// reverts with no code at the address). Add the override back per chain only
// once Multicall3 is deployed there (canonical address
// 0xcA11bde05977b3631167028862bE2a173976CA11 via the standard deterministic
// deployer).

// The testnet is Base Sepolia (84532), over http only: live updates come from
// the blockpot-service event stream (src/lib/eventStream.ts), not websocket
// subscriptions, and the public Base Sepolia RPC has no websocket endpoint.
// The RPC URL stays env-driven (VITE_TESTNET_RPC_URL) so one build can target
// localhost or a public RPC without rebuilding. viem's baseSepolia already
// carries Multicall3 (deployed there) and the Basescan explorer; only the RPC
// is swapped for the configured one.
const baseSepoliaTestnet: Chain = {
    ...baseSepolia,
    rpcUrls: {
        default: { http: [ENV.TESTNET_RPC_URL] },
    },
    blockExplorers: {
        default: { name: 'Basescan', url: 'https://sepolia.basescan.org' },
    },
}

// VITE_TESTNET_CHAIN_ID names no other testnet, so a different value is a
// misconfigured build: fail loudly rather than connect to the wrong chain.
if (ENV.TESTNET_CHAIN_ID !== baseSepolia.id) {
    throw new Error(
        `Unsupported VITE_TESTNET_CHAIN_ID ${ENV.TESTNET_CHAIN_ID}: only ${baseSepolia.id} (Base Sepolia) is supported`,
    )
}

export const testnetChain: Chain = baseSepoliaTestnet

const localAnvil: Chain = {
    ...hardhat,
    name: 'Local Anvil',
}

const CHAINS_BY_MODE = {
    LOCAL: [localAnvil] as const,
    STAGING: [testnetChain] as const,
    PROD: [mainnet, testnetChain] as const,
} satisfies Record<typeof ENV.APP_MODE, readonly Chain[]>

export const ACTIVE_CHAINS = CHAINS_BY_MODE[ENV.APP_MODE]

export const ACTIVE_CHAIN_IDS = ACTIVE_CHAINS.map((c) => c.id)

// Public chains the service may answer for even when this build does not connect to them, so a
// chain id it echoes still gets a readable name.
const KNOWN_CHAINS: readonly Chain[] = [mainnet, base, baseSepolia]

export function chainName(id: number): string {
    return (
        ACTIVE_CHAINS.find((c) => c.id === id)?.name ??
        KNOWN_CHAINS.find((c) => c.id === id)?.name ??
        `chain ${id}`
    )
}

export function explorerUrl(
    chainId: number,
    kind: 'tx' | 'address',
    value: string,
): string | undefined {
    const chain = ACTIVE_CHAINS.find((c) => c.id === chainId)
    const base = chain?.blockExplorers?.default?.url
    if (!base) return undefined
    return `${base.replace(/\/$/, '')}/${kind}/${value}`
}
